import axios, { AxiosInstance, InternalAxiosRequestConfig, AxiosResponse, AxiosError } from 'axios';
import { captureApiError } from '../utils/errorCapture';
import { installStepUpInterceptor } from './stepUpInterceptor';

/**
 * API Configuration for Goodstuff Portal
 *
 * URL Structure: {tenant}.bcportal.app/{company}/{page}
 * Example: goodstuff365.bcportal.app/Cronus/dashboard
 *
 * - Tenant comes from subdomain (handled by TenantContext)
 * - Company comes from first path segment (handled by CompanyContext)
 * - API is served from same origin: {tenant}.bcportal.app/api/*
 */

/**
 * Extract tenant subdomain from hostname
 */
const getTenantSubdomain = (): string | null => {
  const hostname = window.location.hostname;

  if (hostname.endsWith('.bcportal.app')) {
    return hostname.replace('.bcportal.app', '');
  }
  if (hostname.endsWith('.localhost')) {
    return hostname.replace('.localhost', '');
  }
  if (hostname === 'localhost' || hostname.startsWith('127.0.0.1')) {
    return localStorage.getItem('dev_tenant_override') || 'goodstuff365';
  }
  return null;
};

/**
 * Extract company slug from URL path
 * URL format: /{company}/... (e.g., /CRONUS%20International%20Ltd./dashboard)
 */
const getCompanySlug = (): string | null => {
  const path = window.location.pathname;
  const parts = path.split('/').filter(p => p.length > 0);

  if (parts.length > 0) {
    try {
      return decodeURIComponent(parts[0]);
    } catch (_e) {
      return parts[0];
    }
  }
  return null;
};

// Get tenant and company from URL
const tenantSubdomain = getTenantSubdomain();
const companySlug = getCompanySlug();

// API base path is always /api (tenant comes from subdomain)
const API_BASE_PATH = '/api';

/**
 * Get API base URL
 * - Development: localhost:8787 (Workers dev server)
 * - Production: Same origin (subdomain serves both frontend and API)
 */
const getApiBaseUrl = (): string => {
  // Allow override via environment variable
  const envUrl = import.meta.env.VITE_API_URL;
  if (envUrl) return envUrl;

  // Development: Use Workers dev server
  if (import.meta.env.DEV) {
    return 'http://localhost:8787';
  }

  // Production: API is on same origin (subdomain routing)
  // goodstuff365.bcportal.app/api/* is handled by Workers
  return '';
};

const API_BASE_URL = getApiBaseUrl();

/**
 * Session transport (A3).
 *  - 'cookie': the API sets HttpOnly cookies and never hands tokens to JS. Used
 *    whenever the API is same-site with the page (production subdomains serve
 *    both; local dev is localhost:3000 → localhost:8787).
 *  - 'bearer': legacy localStorage tokens + Authorization header. Used when the
 *    API is on another site (VITE_API_URL pointing at another host),
 *    where third-party cookies would be blocked.
 * Override with VITE_AUTH_MODE=cookie|bearer.
 */
export type AuthMode = 'cookie' | 'bearer';
const resolveAuthMode = (): AuthMode => {
  const forced = import.meta.env.VITE_AUTH_MODE;
  if (forced === 'cookie' || forced === 'bearer') return forced;
  if (!API_BASE_URL) return 'cookie';
  try {
    return new URL(API_BASE_URL).hostname === window.location.hostname ? 'cookie' : 'bearer';
  } catch {
    return 'bearer';
  }
};
const AUTH_MODE: AuthMode = resolveAuthMode();
const isCookieMode = (): boolean => AUTH_MODE === 'cookie';

// Create axios instance
const api: AxiosInstance = axios.create({
  baseURL: API_BASE_URL ? API_BASE_URL + API_BASE_PATH : API_BASE_PATH,
  timeout: 30000,
  withCredentials: isCookieMode(),
  headers: {
    'Content-Type': 'application/json',
  },
});

// Token storage keys (bearer mode only; cookie mode keeps nothing in storage)
const TOKEN_KEY = 'token';
const REFRESH_TOKEN_KEY = 'refreshToken';

const lsGet = (k: string): string | null => { try { return localStorage.getItem(k); } catch { return null; } };
const lsSet = (k: string, v: string): void => { try { localStorage.setItem(k, v); } catch { /* quota */ } };
const lsDel = (k: string): void => { try { localStorage.removeItem(k); } catch { /* ignore */ } };

/** Bearer access token, or null in cookie mode (the token lives in an HttpOnly cookie). */
const getToken = (): string | null => (isCookieMode() ? null : lsGet(TOKEN_KEY));
const setToken = (token: string): void => { if (!isCookieMode()) lsSet(TOKEN_KEY, token); };
const removeToken = (): void => { lsDel(TOKEN_KEY); csrfFromBody = null; };
const getRefreshToken = (): string | null => (isCookieMode() ? null : lsGet(REFRESH_TOKEN_KEY));
const setRefreshToken = (token: string): void => { if (!isCookieMode()) lsSet(REFRESH_TOKEN_KEY, token); };
const removeRefreshToken = (): void => lsDel(REFRESH_TOKEN_KEY);

// ── CSRF (cookie mode): double-submit token ──
let csrfFromBody: string | null = null;
/** Current CSRF token: the readable bcp_csrf cookie, else the last one the API returned. */
const getCsrfToken = (): string | null => {
  try {
    const m = document.cookie.match(/(?:^|;\s*)(?:__Host-)?bcp_csrf=([^;]+)/);
    if (m) return decodeURIComponent(m[1]);
  } catch { /* no document */ }
  return csrfFromBody;
};
const UNSAFE_METHODS = new Set(['post', 'put', 'patch', 'delete']);

/**
 * Headers + credentials for code that uses fetch() instead of the axios client.
 * Bearer mode adds Authorization; cookie mode sends cookies plus the CSRF header.
 */
const authFetchInit = (method = 'GET'): { credentials: RequestCredentials; headers: Record<string, string> } => {
  const headers: Record<string, string> = {};
  const sub = getTenantSubdomain();
  if (sub) headers['X-Tenant-Subdomain'] = sub;
  if (isCookieMode()) {
    headers['X-Auth-Mode'] = 'cookie';
    const csrf = getCsrfToken();
    if (csrf && UNSAFE_METHODS.has(method.toLowerCase())) headers['X-CSRF-Token'] = csrf;
    return { credentials: 'include', headers };
  }
  const token = getToken();
  if (token) headers.Authorization = `Bearer ${token}`;
  return { credentials: 'same-origin', headers };
};

/**
 * Rollout: a user signed in before cookie sessions has tokens in localStorage.
 * Exchange the legacy refresh token for cookies once, then wipe the storage.
 */
let legacyMigration: Promise<void> | null = null;
const migrateLegacySession = (): Promise<void> => {
  if (legacyMigration) return legacyMigration;
  legacyMigration = (async () => {
    if (!isCookieMode()) return;
    const legacyRefresh = lsGet(REFRESH_TOKEN_KEY);
    const hadLegacy = legacyRefresh || lsGet(TOKEN_KEY);
    if (!hadLegacy) return;
    lsDel(TOKEN_KEY);
    lsDel(REFRESH_TOKEN_KEY);
    if (!legacyRefresh) return;
    try {
      const sub = getTenantSubdomain();
      const res = await axios.post<{ csrfToken?: string }>(`${API_BASE_URL}${API_BASE_PATH}/auth/refresh`, { refreshToken: legacyRefresh }, {
        withCredentials: true,
        headers: { 'X-Auth-Mode': 'cookie', ...(sub ? { 'X-Tenant-Subdomain': sub } : {}) },
      });
      if (res.data?.csrfToken) csrfFromBody = res.data.csrfToken;
    } catch {
      /* legacy session expired: the user signs in again */
    }
  })();
  return legacyMigration;
};

/** Run one refresh at a time; returns true when a new session is in place. */
let refreshInFlight: Promise<boolean> | null = null;
const refreshSessionOnce = (): Promise<boolean> => {
  if (!refreshInFlight) {
    refreshInFlight = doRefresh().finally(() => { refreshInFlight = null; });
  }
  return refreshInFlight;
};
const doRefresh = async (): Promise<boolean> => {
  const url = `${API_BASE_URL}${API_BASE_PATH}/auth/refresh`;
  const sub = getTenantSubdomain();
  const tenantHeader: Record<string, string> = sub ? { 'X-Tenant-Subdomain': sub } : {};
  try {
    if (isCookieMode()) {
      const csrf = getCsrfToken();
      const response = await axios.post<{ success?: boolean; csrfToken?: string }>(url, {}, {
        withCredentials: true,
        headers: { 'X-Auth-Mode': 'cookie', ...(csrf ? { 'X-CSRF-Token': csrf } : {}), ...tenantHeader },
      });
      if (response.data?.csrfToken) csrfFromBody = response.data.csrfToken;
      return !!response.data?.success;
    }
    const refreshToken = getRefreshToken();
    if (!refreshToken) return false;
    const response = await axios.post<{ token?: string; refreshToken?: string }>(url, { refreshToken }, { headers: tenantHeader });
    if (!response.data.token) return false;
    // Store new token pair (rotation — old refresh token is consumed)
    setToken(response.data.token);
    if (response.data.refreshToken) setRefreshToken(response.data.refreshToken);
    return true;
  } catch (refreshError) {
    console.error('[API] Session refresh failed:', refreshError);
    return false;
  }
};

// Request interceptor - Add auth (bearer token or cookie-mode headers) and tenant header
api.interceptors.request.use(
  async (config: InternalAxiosRequestConfig): Promise<InternalAxiosRequestConfig> => {
    if (isCookieMode()) {
      await migrateLegacySession();
      config.headers['X-Auth-Mode'] = 'cookie';
      const csrf = getCsrfToken();
      if (csrf && UNSAFE_METHODS.has((config.method || 'get').toLowerCase())) {
        config.headers['X-CSRF-Token'] = csrf;
      }
    } else {
      const token = getToken();
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
    }

    // Add tenant header for all requests (needed when API is on different domain)
    // In production the API is same-origin ({tenant}.bcportal.app/api); the header matters for dev/other hosts
    const tenantSubdomain = getTenantSubdomain();
    if (tenantSubdomain) {
      config.headers['X-Tenant-Subdomain'] = tenantSubdomain;
    }

    return config;
  },
  (error: AxiosError): Promise<AxiosError> => Promise.reject(error)
);

// Response interceptor - Handle auth errors
api.interceptors.response.use(
  (response: AxiosResponse): AxiosResponse => {
    const csrf = (response.data as { csrfToken?: unknown } | undefined)?.csrfToken;
    if (typeof csrf === 'string') csrfFromBody = csrf;
    return response;
  },
  async (error: AxiosError): Promise<unknown> => {
    const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };

    // 401s from the sign-in steps themselves (wrong password / wrong code) are not
    // session expiry — don't refresh, clear tokens or fire auth:failed for them.
    const isSignInStep = /\/auth\/(login|transfer|refresh|mfa\/challenge|mfa\/enroll\/)/.test(originalRequest?.url ?? '');

    // Handle 401 Unauthorized — try refresh token rotation
    if (error.response?.status === 401 && !originalRequest._retry && !isSignInStep) {
      originalRequest._retry = true;

      // Single-flight: concurrent 401s share one refresh. Refresh tokens rotate,
      // so parallel refreshes would consume each other and log the user out.
      const refreshed = await refreshSessionOnce();
      if (refreshed) {
        if (!isCookieMode()) {
          originalRequest.headers.Authorization = `Bearer ${getToken()}`;
        }
        return api(originalRequest);
      }

      // Clear all tokens and redirect to login
      removeToken();
      removeRefreshToken();

      // Dispatch auth failure event for app to handle
      window.dispatchEvent(new CustomEvent('auth:failed', {
        detail: { message: 'Session expired' }
      }));
    }

    // Handle 403 Forbidden
    if (error.response?.status === 403) {
      console.error('[API] Access forbidden:', (error.response?.data as { error?: string })?.error);
    }

    // Handle 402 Payment Required — subscription enforcement (see workers/src/middleware/subscription.ts)
    if (error.response?.status === 402) {
      const errorCode = (error.response?.data as { error?: string })?.error;
      if (errorCode === 'subscription_inactive' || errorCode === 'subscription_read_only') {
        window.dispatchEvent(new CustomEvent('subscription:denied', {
          detail: error.response.data
        }));
        // Pages and services surface `data.error` in toasts/inline errors, which would show
        // the raw code ("subscription_read_only"). Swap in the server's human message and keep
        // the machine-readable code on `data.code` (SubscriptionGate already has its copy).
        const body = error.response.data as { error?: string; message?: string; code?: string };
        if (body && typeof body.message === 'string' && body.message) {
          body.code = errorCode;
          body.error = body.message;
        }
      }
    }

    // Handle network errors
    if (!error.response) {
      console.error('[API] Network error:', error.message);
    }

    // Capture for support widget context (skip auth endpoints — request body may contain credentials)
    const captureUrl = error.config?.url ?? '';
    if (!captureUrl.includes('/auth/')) {
      const requestBody = (() => {
        try { return error.config?.data ? JSON.parse(error.config.data as string) : undefined; } catch { return error.config?.data; }
      })();
      captureApiError(
        captureUrl,
        error.config?.method ?? 'unknown',
        error.response?.status ?? null,
        requestBody,
        error.response?.data,
        error.message
      );
    }

    return Promise.reject(error);
  }
);

// A8: prompt "Confirm it's you" and retry once on 403 step_up_required
installStepUpInterceptor(api);

// Log configuration
console.log(`[API Config] Tenant: ${tenantSubdomain || 'NOT DETECTED'}`);
console.log(`[API Config] Company: ${companySlug || 'NOT SPECIFIED'}`);
console.log(`[API Config] Base URL: ${api.defaults.baseURL}`);
console.log(`[API Config] Environment: ${import.meta.env.MODE}`);

// Export utilities
/**
 * Store a freshly issued token pair (login, two-step completion, session rotation).
 * No-op in cookie mode: the API already set HttpOnly cookies and sent no tokens.
 */
const storeSession = (token?: string | null, refreshToken?: string | null): void => {
  if (token) setToken(token);
  if (refreshToken) setRefreshToken(refreshToken);
};

/** True when an auth response established a session (token in body, or cookie mode). */
const isSessionResponse = (data: unknown): boolean => {
  const d = data as { token?: unknown; authMode?: unknown } | null | undefined;
  return !!d && (typeof d.token === 'string' || d.authMode === 'cookie');
};

/** Clear every trace of the session held by the client (tokens, CSRF). */
const clearSession = (): void => { removeToken(); removeRefreshToken(); };

export {
  getToken, getRefreshToken, setToken, removeToken, storeSession, clearSession, isSessionResponse,
  getCsrfToken, authFetchInit, isCookieMode, AUTH_MODE, migrateLegacySession,
  tenantSubdomain, companySlug, getTenantSubdomain, getCompanySlug,
};
export default api;
